import { stripDeviceMutState } from "../sync/device-local.js";
import type { Task } from "./types.js";

/**
 * The provider REMOTE LINK on a Task — the identity of the Google Calendar event
 * this row is mirrored by, plus the etag of the copy we last saw.
 *
 * These fields have exactly ONE writer: the Google reconciler, through
 * `TasksRepository.setRemoteLink`. Nothing else may change them — not the UI, not
 * a restore, not the multi-device sync apply — for two reasons that cost real
 * data before this rule existed:
 *
 *  1. The reconciler adopts them WITHOUT bumping `updated_at` (adopting an etag
 *     is not a user edit and must not mark the row dirty). But `updated_at` is
 *     the multi-device sync's dirty marker AND its LWW key, so an adopted link
 *     is invisible to push and loses every tie on apply.
 *  2. Every UI write is a whole-row read-modify-write built from the zustand
 *     store, which is refreshed only at the end of a sync pass — so an edit made
 *     mid-pass wrote a pre-adoption `external_id: null` straight back over a
 *     freshly-linked row.
 *
 * Either way the link was lost, the next push created a SECOND Google event, and
 * the next pull re-imported the orphaned first one as a duplicate task.
 *
 * ORDERING. Because `updated_at` cannot order link changes, the block carries its
 * own monotonic {@link Task.link_rev}, bumped on every IDENTITY change (etag-only
 * writes never bump it). Higher rev wins the whole block. That is what makes all
 * three transitions converge — and a plain "non-null wins" rule could only ever
 * express the first:
 *
 *   - ADD      (null → id)  a device that learns the event id teaches its peers;
 *   - REPLACE  (id → id')   moving a task between Google calendars deletes the
 *                           old event and creates a new one; peers must follow, or
 *                           they PATCH a dead id, 404, and create a THIRD event;
 *   - REMOVE   (id → null)  delete, un-date, nest-as-subtask, remote tombstone and
 *                           restore-from-Trash all UNLINK deliberately. Under a
 *                           grow-only rule that null could never propagate, and the
 *                           stale id came back from the server on the next pull.
 */
export const TASK_REMOTE_LINK_FIELDS = [
  "external_id",
  "ical_uid",
  "etag",
  "source",
  "external_calendar_id",
] as const;

/** The identity half of the link — everything except the device-local `etag`.
 *  A change to any of these is what bumps {@link Task.link_rev}. */
export const TASK_LINK_IDENTITY_FIELDS = [
  "external_id",
  "ical_uid",
  "source",
  "external_calendar_id",
] as const;

/** The remote-link slice of a task. */
export interface TaskRemoteLink {
  external_id: string | null;
  ical_uid: string | null;
  etag: string | null;
  source: string | null;
  external_calendar_id: string | null;
}

/** An unlinked remote link — written when a task is detached from its remote
 *  event (deleted, un-dated, nested, moved away, or restored from Trash). */
export function emptyRemoteLink(): TaskRemoteLink {
  return {
    external_id: null,
    ical_uid: null,
    etag: null,
    source: null,
    external_calendar_id: null,
  };
}

export function remoteLinkOf(task: Task): TaskRemoteLink {
  return {
    external_id: task.external_id ?? null,
    ical_uid: task.ical_uid ?? null,
    etag: task.etag ?? null,
    source: task.source ?? null,
    external_calendar_id: task.external_calendar_id ?? null,
  };
}

/** True when two links name the same remote resource. `etag` is excluded — it
 *  moves constantly and is not part of the identity. */
export function linkIdentityEquals(
  a: Pick<TaskRemoteLink, (typeof TASK_LINK_IDENTITY_FIELDS)[number]>,
  b: Pick<TaskRemoteLink, (typeof TASK_LINK_IDENTITY_FIELDS)[number]>,
): boolean {
  return TASK_LINK_IDENTITY_FIELDS.every((f) => (a[f] ?? null) === (b[f] ?? null));
}

/**
 * Project a Task for transmission: drop the DEVICE-LOCAL fields.
 *
 * `etag` is this device's record of "the remote revision I last saw" — a peer's
 * copy is stale the moment either device pushes, and a stale etag sent as
 * `If-Match` gets a 412, which used to be resolved by overwriting the local edit
 * from Google. The `mut`/`*_acked_mut` counters are this device's change
 * tracking; a peer adopting them would believe it had already synced writes it
 * has never made (see `sync/device-local.ts`).
 *
 * The identity fields DO travel (a peer that lacks them would create a duplicate
 * event), as does `link_rev`, which orders them.
 */
export function stripDeviceLocalLink(task: Task): Task {
  return { ...stripDeviceMutState(task), etag: null };
}

/**
 * Resolve the link block of an INCOMING task (sync / restore) against what this
 * device already holds. Higher {@link Task.link_rev} wins the whole block —
 * including an explicit unlink, whose nulls are meaningful.
 *
 * At EQUAL rev the two sides are describing the same generation, so each field is
 * grow-only: a hole is filled from whichever side has it. That covers upgrades
 * (every pre-`link_rev` row sits at 0) and the ordinary case of a peer that has
 * simply not learned the link yet, without letting its emptiness erase ours.
 *
 * `etag` is NEVER adopted from the wire, and `local` null = brand-new row here,
 * so the incoming link stands as-is.
 */
export function resolveRemoteLink(incoming: Task, local: Task | null): Task {
  if (!local) return incoming;
  const link = mergeRemoteLink(local, incoming);
  return { ...incoming, ...link, ...deviceLocalOf(local, link) };
}

/**
 * Resolve the link block between two copies, neither authoritative. Returned as a
 * block to graft onto the LWW winner: content and the link are ordered
 * independently, so the row whose content wins is not necessarily the row
 * carrying the newer link.
 *
 * SYMMETRIC — `mergeRemoteLink(a,b)` and `mergeRemoteLink(b,a)` agree, which
 * `mergeRecord` promises and the server depends on (it merges
 * `(canonical, incoming)` while a client merges `(local, remote)`; an
 * order-dependent result lets the two disagree permanently).
 *
 * At EQUAL rev the two sides describe the same generation, so each field is
 * grow-only — a hole is filled from whichever side has it. That covers upgrades
 * (every pre-`link_rev` row sits at 0) and a peer that simply hasn't learned the
 * link yet. When both sides hold DIFFERENT non-null identities at the same rev,
 * two devices really did mint the same generation for different Google events;
 * the smallest `external_id` wins, matching the "lexicographically smallest id
 * survives" rule the entity dedupe already uses, so every device converges.
 *
 * The tie-break only makes the state CONSISTENT — it does not un-create the
 * losing Google event. Preventing that race is the job of the deterministic
 * event id in `map-out`/`push` (`eventIdForTask`), which makes creation
 * idempotent so the second device adopts the first device's event instead of
 * making its own.
 */
export function mergeRemoteLink(a: Task, b: Task): Partial<Task> {
  const aRev = a.link_rev ?? 0;
  const bRev = b.link_rev ?? 0;
  if (aRev !== bRev) {
    const newer = aRev > bRev ? a : b;
    return { ...remoteLinkOf(newer), link_rev: Math.max(aRev, bRev) };
  }
  // Same generation. If BOTH sides name something, the winner is decided over the
  // WHOLE identity tuple — not `external_id` alone. Google event ids are scoped
  // per calendar, so two devices can hold the same id against different
  // calendars; comparing only the id would fall through to a per-field `??`,
  // which picks whichever argument came first. That asymmetry is fatal here: the
  // server merges `(canonical, incoming)` and a client merges `(local, remote)`,
  // so each would keep its own calendar forever.
  if (hasIdentity(a) && hasIdentity(b)) {
    const winner = identityKey(a) <= identityKey(b) ? a : b;
    return { ...remoteLinkOf(winner), link_rev: aRev };
  }
  // At most one side names anything — take that side's block whole. Symmetric by
  // construction, and this is the ordinary "a peer hasn't learned the link yet"
  // case plus the upgrade path (every pre-`link_rev` row sits at 0).
  const known = hasIdentity(a) ? a : hasIdentity(b) ? b : null;
  if (!known) return { ...emptyRemoteLink(), link_rev: aRev };
  return { ...remoteLinkOf(known), link_rev: aRev };
}

function hasIdentity(t: Task): boolean {
  return TASK_LINK_IDENTITY_FIELDS.some((f) => (t[f] ?? null) !== null);
}

/** Stable, total ordering over the identity tuple, for the equal-generation
 *  tie-break. Field order is fixed so both sides compute the same key. */
function identityKey(t: Task): string {
  return TASK_LINK_IDENTITY_FIELDS.map((f) => `${t[f] ?? ""}`).join("\0");
}

/**
 * The DEVICE-LOCAL half of the resolved link. The `etag` describes this device's
 * relationship to the remote resource, never the resource itself, so it is never
 * taken from the wire.
 *
 * The etag is kept only while the identity is UNCHANGED. An etag belongs to one
 * specific event: carrying the old event's etag onto a replacement identity (or
 * onto an unlink) sends a meaningless `If-Match`, which 412s, counts a conflict
 * that never happened, and drags a healthy write through conflict resolution.
 */
function deviceLocalOf(local: Task, resolved: Partial<Task>): Partial<Task> {
  const sameIdentity = linkIdentityEquals(local, {
    external_id: resolved.external_id ?? null,
    ical_uid: resolved.ical_uid ?? null,
    source: resolved.source ?? null,
    external_calendar_id: resolved.external_calendar_id ?? null,
  });
  return {
    etag: sameIdentity ? local.etag ?? null : null,
  };
}
